import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { sentry } from '@/lib/sentry';

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type FallbackProps = {
  error: unknown;
  eventId: string;
  resetError: () => void;
};

const ErrorFallback = ({ error, eventId, resetError }: FallbackProps) => {
  const { t } = useTranslation();
  const isDev = sentry.environment === 'development';

  const errorObject = error instanceof Error ? error : null;

  const handleReload = () => {
    resetError();
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background px-4 py-8">
      <div className="max-w-screen-sm w-full text-center">
        <div className="mx-auto mb-6 flex justify-center">
          <AlertTriangle className="h-20 w-20 text-destructive" />
        </div>
        <p className="mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
          {t('Something went wrong')}
        </p>
        <p className="mb-6 text-lg font-light text-foreground">
          {t(
            'An unexpected error occurred. Our team has been notified. You can try reloading the page.',
          )}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button size="lg" variant="default" onClick={handleReload}>
            {t('Reload page')}
          </Button>
          <Button size="lg" variant="outline" onClick={handleGoHome}>
            {t('Go home')}
          </Button>
        </div>
        {eventId && !isDev && (
          <p className="mt-6 text-sm text-muted-foreground">
            {t('Error code')}: <code className="font-mono">{eventId}</code>
          </p>
        )}
        {isDev && errorObject && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              {t('Error details (dev only)')}
            </summary>
            <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-muted p-4 text-xs">
              {errorObject.message}
              {errorObject.stack ? `\n\n${errorObject.stack}` : ''}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export const AppErrorBoundary = ({ children }: AppErrorBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, eventId, resetError }) => (
        <ErrorFallback
          error={error}
          eventId={eventId}
          resetError={resetError}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
